import { useState, useContext, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { AuthContext } from "../context/AuthContext";

export default function SignUpPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    invite_code: "",
  });

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  // Helper function to format password error messages
  const formatPasswordError = (errorMessage) => {
    if (errorMessage.includes("password") || errorMessage.includes("Password")) {
      // Split the error message by periods and format as bullet points
      const errorParts = errorMessage.split('.').filter(part => part.trim());
      return (
        <div>
          <ul className="text-xs text-red-700 space-y-1">
            {errorParts.map((part, index) => (
              <li key={index}>• {part.trim()}</li>
            ))}
          </ul>
        </div>
      );
    }
    return <p className="text-sm text-red-800">{errorMessage}</p>;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/api/v1/auth/register/`,
        formData
      );
      
      // Check if Firebase custom token is available
      const { custom_token, warning } = response.data;
      
      if (custom_token) {
        // Use Firebase authentication
        const success = await login(custom_token);
        
        if (success) {
          // Show success message briefly
          setSuccess(true);
          setLoading(false);
          // Keep form data - navigation will happen via useEffect
        } else {
          setError("Authentication failed after registration. Please log in manually.");
          setLoading(false);
          // Redirect to login page after a delay
          setTimeout(() => {
            navigate("/login");
          }, 3000);
        }
      } else {
        // Firebase not available - show warning and redirect to login
        if (warning) {
          setError(warning + " Please log in with your credentials.");
        } else {
          setError("Registration successful but authentication failed. Please log in.");
        }
        setLoading(false);
        // Redirect to login page after a delay
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data) {
        // Handle validation errors with better formatting
        const errorMessages = [];
        
        // Handle specific field errors - check if they exist and are arrays
        if (err.response.data.email && Array.isArray(err.response.data.email)) {
          errorMessages.push(...err.response.data.email);
        }
        if (err.response.data.password && Array.isArray(err.response.data.password)) {
          errorMessages.push(...err.response.data.password);
        }
        if (err.response.data.first_name && Array.isArray(err.response.data.first_name)) {
          errorMessages.push(...err.response.data.first_name);
        }
        if (err.response.data.last_name && Array.isArray(err.response.data.last_name)) {
          errorMessages.push(...err.response.data.last_name);
        }
        if (err.response.data.invite_code && Array.isArray(err.response.data.invite_code)) {
          errorMessages.push(...err.response.data.invite_code);
        }
        if (err.response.data.permit_limit && Array.isArray(err.response.data.permit_limit)) {
          errorMessages.push(...err.response.data.permit_limit);
        }
        
        // If we found specific field errors, display them
        if (errorMessages.length > 0) {
          // Join messages with proper punctuation - avoid double periods
          const formattedErrors = errorMessages.map(msg => msg.trim().replace(/\.$/, '')).join('. ');
          setError(formattedErrors + '.');
        } else {
          // Fallback for other error formats
          setError("Registration failed. Please check your details and try again.");
        }
      } else if (err.response?.status === 429) {
        setError("Too many registration attempts. Please wait a moment and try again.");
      } else if (err.response?.status >= 500) {
        setError("Server error. Please try again later or contact support.");
      } else {
        setError("Registration failed. Please check your details and try again.");
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex flex-col items-center">
          <img
            src="/logo_assets/android-chrome-192x192.png"
            alt="ResidentPass Logo"
            className="h-16 sm:h-20 w-auto mb-3 sm:mb-4"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-customIndigo mb-1 sm:mb-2">ResidentPass</h1>
          <p className="text-gray-600 text-xs sm:text-sm">Create your account</p>
        </div>
      </div>

      <div className="mt-6 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-6 sm:py-8 px-4 shadow-xl rounded-lg sm:px-10">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    {formatPasswordError(error)}
                  </div>
                </div>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-800">Registration successful! Redirecting to dashboard...</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="invite_code" className="block text-xs sm:text-sm font-medium text-gray-700">
                Invite code <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <input
                  id="invite_code"
                  name="invite_code"
                  type="text"
                  value={formData.invite_code}
                  onChange={handleChange}
                  required
                  disabled={loading || success}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-customIndigo focus:border-customIndigo text-sm sm:text-base transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">
                This is the token provided by email or letter from your site administrator.
              </p>
            </div>

            <div>
              <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700">
                Email address <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading || success}
                  autoComplete="email"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-customIndigo focus:border-customIndigo text-sm sm:text-base transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="first_name" className="block text-xs sm:text-sm font-medium text-gray-700">
                  First name <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <input
                    id="first_name"
                    name="first_name"
                    type="text"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    disabled={loading || success}
                    autoComplete="given-name"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-customIndigo focus:border-customIndigo text-sm sm:text-base transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="last_name" className="block text-xs sm:text-sm font-medium text-gray-700">
                  Last name <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <input
                    id="last_name"
                    name="last_name"
                    type="text"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    disabled={loading || success}
                    autoComplete="family-name"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-customIndigo focus:border-customIndigo text-sm sm:text-base transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading || success}
                  autoComplete="new-password"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-customIndigo focus:border-customIndigo text-sm sm:text-base transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Password must be at least 8 characters long and contain uppercase, lowercase, and numeric characters.
              </p>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || success}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-customIndigo hover:bg-customIndigo-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-customIndigo disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {loading ? (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : success ? (
                  <svg className="-ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : null}
                {loading ? "Creating account..." : success ? "Success!" : "Create account"}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-medium text-customIndigo hover:text-customIndigo-dark transition-colors duration-200"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
